/**
 * Who reacts: choose a saved audience, or generate one for this kind of message (decision 6.1).
 *
 * Generating costs real model calls, so the price is shown before the button (6.3) and the size is
 * chosen within the plan's ceiling (6.7). A build runs in the background like any other job; the
 * audience row carries its own progress, so this follows the audience rather than the job.
 */

import { Check, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  type Audience,
  useAudience,
  useAudienceCost,
  useAudiences,
  useBuildAudience,
  useGroundings,
  useMe,
} from "@/lib/queries";

import { useDraft } from "./draft";

const MIN_PERSONAS = 10;

export function AudienceDialog({ onDone }: { onDone: () => void }) {
  const { draft, update } = useDraft();
  const audiences = useAudiences();
  const [tab, setTab] = useState<"saved" | "new">("saved");
  const [chosen, setPicked] = useState(draft.audienceId);
  // Nothing chosen yet: start on the first (the general public), so one click confirms the usual case.
  const picked = chosen ?? audiences.data?.[0]?.audience_id ?? null;

  function use(audience: Audience | undefined) {
    update({ audienceId: audience?.audience_id ?? null, audienceName: audience?.name ?? null });
    onDone();
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Who reacts?</DialogTitle>
        <DialogDescription>
          An audience is a saved community of personas, generated and validated once and reused across studies. Pick
          one, or generate one designed for this kind of scenario.
        </DialogDescription>
      </DialogHeader>

      <div role="tablist" aria-label="Audience" className="flex gap-4 border-b border-white/10 text-sm">
        <Tab id="saved" current={tab} onSelect={setTab}>
          Choose saved
        </Tab>
        <Tab id="new" current={tab} onSelect={setTab}>
          Generate new
        </Tab>
      </div>

      {tab === "saved" ? (
        <>
          <div role="radiogroup" aria-label="Audiences" className="grid max-h-80 gap-2 overflow-y-auto">
            {(audiences.data ?? []).map((a) => (
              <button
                key={a.audience_id}
                type="button"
                role="radio"
                aria-checked={picked === a.audience_id}
                disabled={a.status !== "ready"}
                onClick={() => setPicked(a.audience_id)}
                className={cn(
                  "rounded-lg border p-3 text-left transition-colors disabled:opacity-60",
                  picked === a.audience_id ? "border-step-done bg-step-done/10" : "border-white/10 hover:bg-white/5",
                )}
              >
                <span className="flex items-center justify-between font-medium">
                  <span>
                    {a.name}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">{describe(a)}</span>
                  </span>
                  {picked === a.audience_id && <Check className="size-4 text-step-done" aria-hidden="true" />}
                </span>
                {a.description && <span className="mt-1 block text-sm text-muted-foreground">{a.description}</span>}
                {a.status === "failed" && a.error && (
                  <span className="mt-1 block text-sm text-destructive">{a.error}</span>
                )}
                <FitNote audience={a} />
              </button>
            ))}
            {audiences.isLoading && <p className="text-sm text-muted-foreground">Loading audiences…</p>}
          </div>
          <DialogFooter>
            <Button
              disabled={!picked}
              onClick={() => use(audiences.data?.find((a) => a.audience_id === picked))}
            >
              Use this audience
            </Button>
          </DialogFooter>
        </>
      ) : (
        <GenerateAudience onReady={use} />
      )}
    </DialogContent>
  );
}

function Tab({
  id,
  current,
  onSelect,
  children,
}: {
  id: "saved" | "new";
  current: string;
  onSelect: (id: "saved" | "new") => void;
  children: string;
}) {
  const active = current === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onSelect(id)}
      className={cn(
        "-mb-px border-b-2 px-1 pb-2",
        active ? "border-step-next font-medium text-foreground" : "border-transparent text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

function describe(a: Audience): string {
  if (a.builtin) return "built-in · personas generated per study";
  if (a.status === "building") return `building… ${a.stage}`;
  if (a.status === "failed") return "failed";
  const drawn = a.grounding && a.grounding.source_id !== "balanced" ? ` · ${a.grounding.name}` : "";
  return `${a.persona_count} personas · ${a.mode}${drawn}`;
}

const VARIABLE_NAMES: Record<string, string> = {
  age_range: "age",
  education_level: "education",
  occupation_category: "occupation",
  region_type: "region",
};

/** What was measured, in the product's own words - never a claim about predicting real opinion. */
function FitNote({ audience }: { audience: Audience }) {
  const grounding = audience.grounding;
  if (!grounding || grounding.source_id === "balanced" || !grounding.fit?.length) return null;
  const worst = Math.max(...grounding.fit.map((f) => f.points));
  return (
    <div className="mt-2 space-y-1 border-t border-white/10 pt-2 text-xs text-muted-foreground">
      <p>
        Drawn from <span className="text-foreground">{grounding.name}</span>. Its{" "}
        {grounding.fit.map((f) => VARIABLE_NAMES[f.variable] ?? f.variable).join(", ")} match that
        population within <span className="text-foreground">{worst.toFixed(1)} percentage points</span>.
      </p>
      {grounding.assumptions?.length ? (
        <details>
          <summary className="cursor-pointer">What this does and does not mean</summary>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {grounding.assumptions.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="mt-1">{grounding.attribution}</p>
        </details>
      ) : null}
    </div>
  );
}

function GenerateAudience({ onReady }: { onReady: (audience: Audience) => void }) {
  const me = useMe().data;
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [count, setCount] = useState(MIN_PERSONAS);
  const [live, setLive] = useState(false);
  const [grounding, setGrounding] = useState("balanced");
  const groundings = useGroundings();
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const cost = useAudienceCost(count, live ? "live" : "mock");
  const building = useAudience(buildingId);
  const start = useBuildAudience();
  const ceiling = cost.data?.max_personas ?? MIN_PERSONAS;

  useEffect(() => {
    if (building.data?.status === "ready") {
      toast.success(`${building.data.name} is ready`);
      onReady(building.data);
    }
  }, [building.data, onReady]);

  if (buildingId) {
    const audience = building.data;
    return (
      <div className="space-y-3 py-2">
        {audience?.status === "failed" ? (
          <Alert variant="destructive">
            <AlertDescription>{audience.error ?? "the audience could not be built"}</AlertDescription>
          </Alert>
        ) : (
          <p className="flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            <span aria-live="polite">{audience?.stage || "queued…"}</span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          This runs in the background - you can close this and carry on. It appears under "Choose saved" when it is
          ready.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setBuildingId(null)}>
            Back
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        start.mutate(
          {
            name: name.trim(),
            category: "",
            grounding,
            instructions: instructions.trim(),
            persona_count: count,
            mode: live ? "live" : "mock",
          },
          {
            onSuccess: (made) => setBuildingId(made.audience.audience_id),
            onError: (error: unknown) => toast.error(error instanceof Error ? error.message : "could not start"),
          },
        );
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="audience-name">Name</Label>
        <Input
          id="audience-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="UK renters"
          maxLength={60}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audience-instructions">Who should be in this audience?</Label>
        <Textarea
          id="audience-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="People who rent privately in UK cities, including those on low incomes and recent movers."
          rows={4}
          maxLength={2000}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audience-count">Personas</Label>
        <Input
          id="audience-count"
          type="number"
          min={MIN_PERSONAS}
          max={ceiling}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
        />
        <p className="text-xs text-muted-foreground">
          Between {MIN_PERSONAS} and {ceiling} on your plan. Built and validated once, then reused: each study draws
          its own number of personas from it.
        </p>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Draw these people from</legend>
        {(groundings.data ?? []).map((choice) => (
          <label
            key={choice.id}
            className={cn(
              "flex cursor-pointer gap-2 rounded-lg border p-2 text-sm",
              grounding === choice.id ? "border-step-done bg-step-done/10" : "border-white/10 hover:bg-white/5",
            )}
          >
            <input
              type="radio"
              name="grounding"
              className="mt-1"
              checked={grounding === choice.id}
              onChange={() => setGrounding(choice.id)}
            />
            <span>
              <span className="font-medium">{choice.name}</span>
              {choice.source && <span className="mt-0.5 block text-xs text-muted-foreground">{choice.source}</span>}
              {choice.licence && (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {choice.licence} · {choice.variables?.map((v) => VARIABLE_NAMES[v] ?? v).join(", ")} are drawn from
                  it; the rest is left to the model
                </span>
              )}
            </span>
          </label>
        ))}
      </fieldset>

      {me?.live_runs_allowed && (
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={live} onCheckedChange={setLive} aria-label="Use the real model" />
          Use the real model (costs credit)
        </label>
      )}

      {live ? (
        <p className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm">
          About <b>${cost.data?.likely_low_usd.toFixed(2)}</b> to <b>${cost.data?.likely_high_usd.toFixed(2)}</b> in
          credit, with <b>${cost.data?.reserve_usd.toFixed(2)}</b> set aside until it finishes.{" "}
          {cost.data && !cost.data.affordable && (
            <span className="text-destructive">
              That is more than your ${cost.data.available_usd.toFixed(2)} of credit.
            </span>
          )}
        </p>
      ) : (
        <p className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-muted-foreground">
          Mock mode: no model is called and nothing is charged. The personas come from hand-written rules, so use it
          to learn the shape of an audience, not to judge a message.
        </p>
      )}

      {start.error && (
        <Alert variant="destructive">
          <AlertDescription>{(start.error as Error).message}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="submit" disabled={start.isPending || (live && cost.data?.affordable === false)}>
          <Sparkles aria-hidden="true" />
          {start.isPending ? "Starting…" : "Generate audience"}
        </Button>
      </DialogFooter>
    </form>
  );
}
